import { vesselScheduleStrings } from '@data/vesselSchedule';
import { formStrings } from '@utils/forms';
import { Either } from 'fp-ts/lib/Either';
import { SafeParseReturnType } from 'zod';

export class D2DError extends Error {}

export class FormError extends D2DError {
  constructor(
    public message: string,
    public dict: Record<string, string | string[] | undefined | null>,
  ) {
    super(`${message}: ${dict}`);
  }

  getDisplayMessage() {
    return [...Object.entries(this.dict)]
      .flatMap(([key, value]) => {
        if (typeof value === 'string') {
          return `${key === 'non_field_errors' ? formStrings.error : key}: ${value}`;
        } else if (Array.isArray(value)) {
          return `${key === 'non_field_errors' ? formStrings.error : key}: ${value.join(',')}`;
        } else {
          return [];
        }
      })
      .join(';');
  }

  getDisplayMessageLegacy() {
    return [...Object.values(this.dict)]
      .flat()
      .filter((x) => x && typeof x === 'string')
      .join(' ');
  }
}

export class JwtExpiredError extends D2DError {}

export class MessageError extends D2DError {}

export class CodeError<T> extends D2DError {
  constructor(public code: T) {
    super(`${code}`);
  }
}

export class NotFoundError extends D2DError {}

export class InvalidSearch extends D2DError {}

export class VesselScheduleUpdateFailureError extends D2DError {
  constructor(public message: string, public arr: string[]) {
    super(`${message}: ${arr}`);
  }

  getDisplayMessage() {
    return vesselScheduleStrings.cannotUpdateVessel(this.arr);
  }
}

export class EmailDuplicateError extends D2DError {}

export type MessagesEitherLeft = { messages: string[] };

export type MessagesEitherRight = true;

export type MessagesEither = Either<MessagesEitherLeft, true>;

export const firstLeftEither = <E, A>(...getEithers: (() => Either<E, A>)[]): Either<E, A> => {
  let either!: Either<E, A>;
  for (const getEither of getEithers) {
    either = getEither();
    if (either._tag === 'Left') {
      return either;
    }
  }
  return either;
};

export const mergeMessagesEither = (...getEithers: (() => MessagesEither)[]): MessagesEither => {
  const left: MessagesEitherLeft = { messages: [] };

  for (const getEither of getEithers) {
    const either = getEither();
    if (either._tag === 'Left') {
      left.messages.push(...either.left.messages);
    }
  }
  if (left.messages.length) {
    return makeMessagesEitherLeft(left);
  } else {
    return makeMessagesEitherRight();
  }
};

export const makeMessagesEitherLeft = (left: MessagesEitherLeft): MessagesEither => {
  return {
    _tag: 'Left',
    left,
  };
};

export const makeMessagesEitherRight = (): MessagesEither => {
  return {
    _tag: 'Right',
    right: true,
  };
};

export const mapMessagesEitherLeftOr = (
  either: MessagesEither,
  map: (left: MessagesEitherLeft) => MessagesEither,
) => {
  if (either._tag === 'Left') {
    return map(either.left);
  } else {
    return either;
  }
};

export const makeMessagesEitherRightIf = (
  condition: boolean,
  getLeftOrLeft: MessagesEitherLeft | (() => MessagesEitherLeft),
): MessagesEither => {
  if (condition) {
    return makeMessagesEitherRight();
  } else {
    return makeMessagesEitherLeft(
      getLeftOrLeft instanceof Function ? getLeftOrLeft() : getLeftOrLeft,
    );
  }
};

export const makeMessagesEitherLeftIf: typeof makeMessagesEitherRightIf = (
  condition,
  getLeftOrLeft,
) => makeMessagesEitherRightIf(!condition, getLeftOrLeft);

export const makeMessagesEitherFromZod = (r: SafeParseReturnType<any, any>): MessagesEither => {
  if (r.success) {
    return makeMessagesEitherRight();
  } else {
    return makeMessagesEitherLeft({
      messages: r.error.issues.map((i) => i.message),
    });
  }
};

export const foldMessagesEither = <R>(
  e: MessagesEither,
  onLeft: (e: MessagesEitherLeft) => R,
  onRight: (e: MessagesEitherRight) => R,
) => {
  if (e._tag === 'Left') {
    return onLeft(e.left);
  } else {
    return onRight(e.right);
  }
};
